import type { ReactNode } from 'react';
import { Children, Fragment } from 'react';
import styled from 'styled-components';

import { Portal } from 'atoms/layout/Portal';
import { Vertical } from 'atoms/layout/flex';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useScreenSize } from 'hooks/device/useScreenSize';

const Root = styled.div`
  --aside-width: 380px;
  display: grid;
  grid-template-columns: calc(100% - var(--aside-width)) var(--aside-width);
  grid-template-areas: 'main aside';
`;

const Main = styled.div`
  position: relative;
  grid-area: main;
  padding-bottom: var(--triple-unit);
`;
const Aside = styled(Vertical)`
  grid-area: aside;
  position: sticky;
  top: var(--current-stack-height, 0);
  height: calc(100vh - var(--current-stack-height, 0));
  overflow: auto;
`;

const Footer = styled.div`
  position: sticky;
  bottom: 0;
`;

type Props = {
  aside: ReactNode[];
  children: ReactNode;
  footer?: ReactNode;
};

export const PlayerPageAsideLayout = ({ aside, children, footer }: Props) => {
  const { up: isDesktopOrAbove } = useScreenSize('desktop');
  const { up: isLaptopOrAbove } = useScreenSize('laptop');
  const { currentUser } = useCurrentUserContext();
  const isLoggedIn = !!currentUser;
  if (isDesktopOrAbove)
    return (
      <Root>
        <Main>
          <Vertical>{children}</Vertical>
        </Main>
        <Aside>
          {Children.map(aside, (asideChild, index) => {
            if (!asideChild) return null;
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={index}>{asideChild}</Fragment>
            );
          })}
        </Aside>
      </Root>
    );
  return (
    <Vertical>
      {children}
      {!isLaptopOrAbove && isLoggedIn ? (
        <Portal id="above-bottom-bar-portal">{footer}</Portal>
      ) : (
        <Footer>{footer}</Footer>
      )}
    </Vertical>
  );
};
